'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Slot } from '../slot';
import { UpsellRootContext } from './context/upsell-root.context';
import { upsellCloseButtonStyles } from './styles/upsell-close-button.styles';
import type { UpsellCloseButtonPropsInterface } from './types/upsell-close-button.types';

/**
 * @since 10.0.0
 */
export const UpsellCloseButton = forwardRef<HTMLButtonElement, UpsellCloseButtonPropsInterface>(
  (props: UpsellCloseButtonPropsInterface, forwardedRef) => {
    const { tone, open, onOpenChange } = useContext(UpsellRootContext);
    const {
      children = <CloseButton onImpact={tone && ['hero', 'impact'].includes(tone)} />,
      className,
      css: cssProp,
      onClick,
      ...rest
    } = props;

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        if (open) {
          onOpenChange?.(event);
        }

        onClick?.(event);
      },
      [open, onOpenChange, onClick],
    );
    const isNativeElement = typeof children.type === 'string';

    return (
      <Slot
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Upsell.CloseButton"
        {...(isNativeElement
          ? {
              className: cx(
                'c11n_upsell__close-button',
                css(upsellCloseButtonStyles, cssProp),
                className,
              ),
            }
          : {
              css: { ...upsellCloseButtonStyles, ...cssProp },
              className: cx('c11n_upsell__close-button', className),
            })}
        {...rest}
        onClick={onClickHandler}
      >
        {children}
      </Slot>
    );
  },
);

UpsellCloseButton.displayName = 'Upsell.CloseButton';
